import html2pdf from "html2pdf.js";
import { formatKolkataDate } from "./dateTime";

const POINT_WALLET_KEYS = new Set(["tools_wallet", "gift_card_wallet", "reserve_wallet"]);

const WALLET_LABELS = {
  main_wallet: "Main Wallet",
  tools_wallet: "Tools Point",
  gift_card_wallet: "Gift Card",
  reserve_wallet: "Skill Point",
};

function downloadBlob(content, fileName, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normalizeText(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatementDateValue(value) {
  const time = new Date(value || "").getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function isPointWallet(walletType) {
  return POINT_WALLET_KEYS.has(String(walletType || "main_wallet"));
}

export function formatWalletName(walletType) {
  return WALLET_LABELS[String(walletType || "main_wallet")] || normalizeText(walletType || "main_wallet");
}

export function formatStatementPurpose(row) {
  if (row?.note) {
    return row.note;
  }

  if (row?.reference_type) {
    return `${normalizeText(row.reference_type)} transaction`;
  }

  return "Manual or system-generated transaction";
}

export function formatStatementReference(row) {
  if (!row?.reference_type) {
    return "-";
  }

  return row.reference_value || (row.reference_id ? String(row.reference_id) : normalizeText(row.reference_type));
}

export function formatStatementDate(value) {
  return formatKolkataDate(value) || "-";
}

export function formatStatementAmount(amount, walletType) {
  const numericAmount = Number(amount || 0).toFixed(2);
  return isPointWallet(walletType) ? `${numericAmount} Pt` : `Rs ${numericAmount}`;
}

export function sortStatementRows(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((left, right) => {
    const rightTime = getStatementDateValue(right.created_at);
    const leftTime = getStatementDateValue(left.created_at);
    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return Number(right.id || 0) - Number(left.id || 0);
  });
}

export function filterStatementRows(rows, filters) {
  const fromDate = filters?.fromDate || "";
  const toDate = filters?.toDate || "";

  return sortStatementRows(rows).filter((row) => {
    const rowDate = String(row?.created_at || "").slice(0, 10);

    if (fromDate && rowDate && rowDate < fromDate) {
      return false;
    }

    if (toDate && rowDate && rowDate > toDate) {
      return false;
    }

    return true;
  });
}

export function buildStatementSummary(rows) {
  return (Array.isArray(rows) ? rows : []).reduce(
    (summary, row) => {
      const amount = Number(row?.amount || 0);

      if (isPointWallet(row?.wallet_type)) {
        if (row?.transaction_type === "credit") {
          summary.totalPointCredit += amount;
        }
        return summary;
      }

      if (row?.transaction_type === "credit") {
        summary.totalCredit += amount;
      } else if (row?.transaction_type === "debit") {
        summary.totalDebit += amount;
      }

      return summary;
    },
    { totalCredit: 0, totalDebit: 0, totalPointCredit: 0 }
  );
}

function buildExportRows(rows, includeCentre) {
  return rows.map((row, index) => ({
    sl: index + 1,
    date: formatStatementDate(row.created_at),
    centre: includeCentre ? row.centre_name || row.login_id || `Franchise ${row.franchise_id}` : "",
    wallet: formatWalletName(row.wallet_type),
    type: normalizeText(row.transaction_type || "-"),
    amount: formatStatementAmount(row.amount, row.wallet_type),
    purpose: formatStatementPurpose(row),
    reference: formatStatementReference(row),
  }));
}

export function downloadStatementCsv({ fileName, rows, summary, includeCentre = false, filters = {} }) {
  const exportRows = buildExportRows(rows, includeCentre);
  const lines = [
    ["Statement Report"],
    [`From Date`, filters.fromDate || "All"],
    [`To Date`, filters.toDate || "All"],
    [`Total Credit`, `Rs ${summary.totalCredit.toFixed(2)}`],
    [`Total Debit`, `Rs ${summary.totalDebit.toFixed(2)}`],
    [`Net Movement`, `Rs ${(summary.totalCredit - summary.totalDebit).toFixed(2)}`],
    [`Total Point Credit`, `${summary.totalPointCredit.toFixed(2)} Pt`],
    [],
    includeCentre
      ? ["Sl No", "Date", "Centre", "Wallet", "Type", "Amount", "Purpose", "Reference"]
      : ["Sl No", "Date", "Wallet", "Type", "Amount", "Purpose", "Reference"],
    ...exportRows.map((row) =>
      includeCentre
        ? [row.sl, row.date, row.centre, row.wallet, row.type, row.amount, row.purpose, row.reference]
        : [row.sl, row.date, row.wallet, row.type, row.amount, row.purpose, row.reference]
    ),
  ];

  const csv = lines
    .map((line) =>
      line
        .map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`)
        .join(",")
    )
    .join("\n");

  downloadBlob(csv, fileName, "text/csv;charset=utf-8;");
}

export function downloadStatementExcel({ fileName, rows, summary, includeCentre = false, filters = {} }) {
  const exportRows = buildExportRows(rows, includeCentre);
  const headerCells = includeCentre
    ? "<th>Sl No</th><th>Date</th><th>Centre</th><th>Wallet</th><th>Type</th><th>Amount</th><th>Purpose</th><th>Reference</th>"
    : "<th>Sl No</th><th>Date</th><th>Wallet</th><th>Type</th><th>Amount</th><th>Purpose</th><th>Reference</th>";
  const bodyRows = exportRows
    .map((row) =>
      includeCentre
        ? `<tr><td>${row.sl}</td><td>${escapeHtml(row.date)}</td><td>${escapeHtml(row.centre)}</td><td>${escapeHtml(row.wallet)}</td><td>${escapeHtml(row.type)}</td><td>${escapeHtml(row.amount)}</td><td>${escapeHtml(row.purpose)}</td><td>${escapeHtml(row.reference)}</td></tr>`
        : `<tr><td>${row.sl}</td><td>${escapeHtml(row.date)}</td><td>${escapeHtml(row.wallet)}</td><td>${escapeHtml(row.type)}</td><td>${escapeHtml(row.amount)}</td><td>${escapeHtml(row.purpose)}</td><td>${escapeHtml(row.reference)}</td></tr>`
    )
    .join("");

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #0f172a; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
          th, td { border: 1px solid #cbd5e1; padding: 8px; font-size: 12px; text-align: left; }
          th { background: #f8fafc; }
          h2 { margin: 0 0 16px; }
        </style>
      </head>
      <body>
        <h2>Statement Report</h2>
        <table>
          <tr><th>From Date</th><td>${escapeHtml(filters.fromDate || "All")}</td><th>To Date</th><td>${escapeHtml(filters.toDate || "All")}</td></tr>
          <tr><th>Total Credit</th><td>Rs ${summary.totalCredit.toFixed(2)}</td><th>Total Debit</th><td>Rs ${summary.totalDebit.toFixed(2)}</td></tr>
          <tr><th>Net Movement</th><td>Rs ${(summary.totalCredit - summary.totalDebit).toFixed(2)}</td><th>Total Point Credit</th><td>${summary.totalPointCredit.toFixed(2)} Pt</td></tr>
        </table>
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
    </html>
  `;

  downloadBlob(html, fileName, "application/vnd.ms-excel;charset=utf-8;");
}

export function downloadStatementPdf({ fileName, element }) {
  if (!element) {
    return;
  }

  html2pdf()
    .set({
      margin: 0.4,
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    })
    .from(element)
    .save();
}
